// Door_ModUI.tsx — Buildings Set1 §8 editor for `Mod_Door`. Surfaces the door's inheritable
// fields (width/height/swing/openAngle) via `InheritableField`, with resolved values +
// provenance from the §3 fallback chain. P2 adds the "bound to wall segment" readout.
import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Row, Select, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_Door} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Door.js";
import {DoorSwing, DoorSwingInfo} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Building/BuildingShared.js";
import {ResolveBuildingDefaults, ResolveDoorParams, FindOwningRoom, FindOwningBuilding} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {ResolveOpeningBinding} from "../../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {InheritableField} from "./Building/InheritableField.js";

export const Door_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, obj} = props;
	const Change = (mutator: (data: Mod_Door)=>any)=>ChangeModWrapper(a=>mutator(a.door!));
	const data = mod.door!;

	const resolved = ResolveDoorParams(obj);
	const resolvedDefaults = ResolveBuildingDefaults(obj);
	const prov = (path: string)=>resolvedDefaults.provenance[path];

	const inheritField = (key: keyof Mod_Door, label: string, number: {min?: number; max?: number; step?: number})=>{
		const resolvedValue = resolved[key] as number;
		const provenance = prov(`door.${key}`);
		return <InheritableField key={key} label={label} info="Inherited via the building fallback chain."
			resolvedValue={resolvedValue} value={data[key] ?? null}
			provenance={provenance}
			onChange={(v)=>Change(a=>{ (a as any)[key] = v as number|n; })}
			number={number}/>;
	};

	const swingOverridden = data.swing != null;
	const swingShown = data.swing ?? resolved.swing;

	// P2 — the "bound to wall segment" readout (or a red "no wall within tolerance" warning).
	// Set2 F3: the binding is FLOOR-level (the merged floor wall), so a door on a shared wall reports
	// the single merged segment both rooms' walls collapsed into.
	const roomOwner = FindOwningRoom(obj);
	const buildingOwner = FindOwningBuilding(obj);
	const bindingRes = ResolveOpeningBinding(obj);
	const binding = bindingRes?.binding ?? null;

	return (
		<>
			<Text style={{color: "#999", fontSize: 12}}>Door opening (sill at floor level) in the closest ancestor Room's wall. Geometry lands in P4.</Text>
			<BindingReadout roomOwner={roomOwner?.obj} buildingOwner={buildingOwner?.obj} binding={binding} segment={bindingRes?.segment}/>
			{inheritField("width", "Width (m)", {min: 0.5, max: 3, step: 0.1})}
			{inheritField("height", "Height (m)", {min: 1.5, max: 4, step: 0.1})}
			{inheritField("openAngle", "Open angle (°)", {min: 0, max: 180, step: 5})}
			<Row mt={5}>
				<TextPlus info={DoorSwingInfo} style={{color: "#ccc"}}>Swing:</TextPlus>
				<Select ml={5} displayType="button bar" options={GetEntries(DoorSwing, "ui")}
					value={swingShown}
					onChange={val=>Change(a=>a.swing = val)}/>
				{!swingOverridden && <Text style={{color: "#777", fontSize: 11}}>(from {prov("door.swing")})</Text>}
			</Row>
		</>
	);
});

/** The shared "bound to wall segment N at X.X m" readout for door/window panels. Set2 F3: the
 *  binding is against the FLOOR's merged wall segments, and the segment's claimants are shown so a
 *  door on a shared (interior) wall is visibly a shared wall. */
export function BindingReadout(props: {roomOwner: any, buildingOwner: any, binding: any, segment?: any}) {
	const {roomOwner, buildingOwner, binding, segment} = props;
	if (!roomOwner || !buildingOwner) {
		return <Text style={{color: "#f88", fontSize: 11}}>⚠ No owning Room/Building — this opening isn't bound to any wall.</Text>;
	}
	if (!binding) {
		return <Text style={{color: "#f88", fontSize: 11}}>⚠ Not near a wall (or no wall within tolerance) — this opening won't punch a hole.</Text>;
	}
	const shared = segment?.shared ? ` (shared wall: ${segment.claimantRoomIDs.join(" & ")})` : "";
	return <Text style={{color: "#9c9", fontSize: 11}}>Bound to: {roomOwner.name || roomOwner.id}, floor wall segment {binding.segmentIndex} at {binding.alongCenter.toFixed(2)} m{shared}.</Text>;
}
